import React, { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material'
import { VolumeUp, Upload, Refresh, Lock, VpnKey } from '@mui/icons-material'
import { useAppSelector } from '@/hooks/redux'
import { API_BASE_URL } from '../config/api'

const Settings: React.FC = () => {
  const { token } = useAppSelector(state => state.auth)
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [customSoundUrl, setCustomSoundUrl] = useState<string | null>(null)
  const [saveMessage, setSaveMessage] = useState('')
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [passwordLoading, setPasswordLoading] = useState(false)

  useEffect(() => {
    const enabled = localStorage.getItem('notificationSoundEnabled')
    const customSound = localStorage.getItem('customNotificationSound')
    
    setSoundEnabled(enabled !== 'false')
    setCustomSoundUrl(customSound)
  }, [])

  const handleToggleSound = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked
    setSoundEnabled(value)
    localStorage.setItem('notificationSoundEnabled', value.toString())
    setSaveMessage('Settings saved successfully')
    setTimeout(() => setSaveMessage(''), 3000)
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('audio/')) {
      setSaveMessage('Please select an audio file')
      setTimeout(() => setSaveMessage(''), 3000)
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string
      setCustomSoundUrl(dataUrl)
      localStorage.setItem('customNotificationSound', dataUrl)
      setSaveMessage('Custom sound uploaded successfully')
      setTimeout(() => setSaveMessage(''), 3000)
    }
    reader.readAsDataURL(file)
  }

  const handleResetSound = () => {
    setCustomSoundUrl(null)
    localStorage.removeItem('customNotificationSound')
    setSaveMessage('Reset to default notification sound')
    setTimeout(() => setSaveMessage(''), 3000)
  }

  const handleTestSound = () => {
    const soundUrl = customSoundUrl || '/notification.mp3'
    const audio = new Audio(soundUrl)
    audio.volume = 0.5
    audio.play().catch(() => {
      setSaveMessage('Failed to play sound. Please check your audio file.')
      setTimeout(() => setSaveMessage(''), 3000)
    })
  }

  const handleChangePassword = async () => {
    setPasswordLoading(true)
    setPasswordError('')
    setPasswordSuccess('')

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required')
      setPasswordLoading(false)
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match')
      setPasswordLoading(false)
      return
    }

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters')
      setPasswordLoading(false)
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        }),
      })

      const data = await response.json()

      if (data.success) {
        setPasswordSuccess('Password changed successfully!')
        setTimeout(() => {
          handleCloseChangePassword()
        }, 2000)
      } else {
        setPasswordError(data.message || 'Failed to change password')
      }
    } catch (err: any) {
      setPasswordError('Failed to change password. Please try again.')
      console.error('Change password error:', err)
    } finally {
      setPasswordLoading(false)
    }
  }

  const handleCloseChangePassword = () => {
    setChangePasswordOpen(false)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
    setPasswordSuccess('')
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Notification Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Customize your message notification sounds
        </Typography>

        {saveMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {saveMessage}
          </Alert>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={soundEnabled}
              onChange={handleToggleSound}
              color="primary"
            />
          }
          label="Enable notification sound for new messages"
        />

        {soundEnabled && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Sound Options
            </Typography>

            <Stack spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Upload />}
                component="label"
              >
                {customSoundUrl ? 'Change Custom Sound' : 'Upload Custom Sound'}
                <input
                  type="file"
                  hidden
                  accept="audio/*"
                  onChange={handleFileUpload}
                />
              </Button>

              {customSoundUrl && (
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleResetSound}
                  color="secondary"
                >
                  Reset to Default Sound
                </Button>
              )}

              <Button
                variant="contained"
                startIcon={<VolumeUp />}
                onClick={handleTestSound}
              >
                Test Sound
              </Button>
            </Stack>

            <Alert severity="info" sx={{ mt: 3 }}>
              Supported formats: MP3, WAV, OGG, M4A
            </Alert>
          </>
        )}
      </Paper>

      {/* Security Settings Section */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Lock color="primary" />
          <Typography variant="h6">
            Security Settings
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Change Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update your password to keep your account secure
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<VpnKey />}
            onClick={() => setChangePasswordOpen(true)}
            sx={{ ml: 2 }}
          >
            Change Password
          </Button>
        </Box>
      </Paper>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onClose={handleCloseChangePassword} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {passwordSuccess}
            </Alert>
          )}
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your current password and set a new secure password.
          </Typography>

          <TextField
            fullWidth
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            sx={{ mb: 2 }}
            autoFocus
          />

          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            sx={{ mb: 2 }}
            helperText="Minimum 8 characters"
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangePassword}>Cancel</Button>
          <Button 
            onClick={handleChangePassword}
            variant="contained"
            disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
          >
            {passwordLoading ? 'Updating...' : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Settings
